import { faClipboardList } from '@fortawesome/free-solid-svg-icons';

/**
 * Bunkers Enquiry (FuelEnquiryForm) walkthrough.
 *
 * Walks the create-enquiry screen end-to-end: header chrome, stepper, the
 * five draft-stage sections (IMT, Customer, Credit, Product, Ship Details)
 * and the two save paths (Save Draft vs Save & Workflow).
 *
 * Bunkers-only - Cargo does not use the enquiry flow.
 */

const NEW_ENQUIRY_ROUTE = '/dashboard/trades/fuels/fuel-enquiries/new';

const bunkersEnquiryTour = {
    id: 'bunkers-enquiry',
    title: 'Bunkers: New Enquiry',
    description:
        'Deep-dive on the bunker enquiry form - IMT entity, customer, credit, product, ship - plus the Save & Workflow handoff.',
    estimatedMinutes: 4,
    startRoute: NEW_ENQUIRY_ROUTE,
    icon: faClipboardList,
    group: 'flow',
    steps: [
        {
            target: '.fuel-enquiry-screen',
            title: 'New Bunker Enquiry',
            content:
                'Every Bunkers deal starts here. A customer asks for a stem at a specific port and date; this form captures everything needed to quote, screen credit and hand off to the supply trader.',
            placement: 'center',
            disableBeacon: true,
        },
        {
            target: '.fef-back-circle',
            title: 'Back to Bunker Enquiries',
            content:
                'Leaving with unsaved changes prompts you to save a draft first - your work is never lost as long as you confirm.',
            placement: 'bottom',
        },
        {
            target: '.fef-stepper-bar',
            title: 'Section stepper',
            content:
                'Five sections at draft stage: IMT, Customer, Credit, Product, Ship. Three more (Supplier, Tonnage, 3rd Party) unlock once the enquiry advances past draft.',
            placement: 'bottom',
        },
        {
            target: '#fef-anchor-hms',
            title: 'IMT entity',
            content:
                'Pick which IMT entity is taking the deal. This drives credit lines, banking instructions and which legal terms apply to the enquiry.',
            placement: 'top',
        },
        {
            target: '#fef-anchor-customer',
            title: 'Customer',
            content:
                'The buyer. Search the CRM for an existing partner; if they are not there, create them in CRM first. The customer drives KYC, AML and credit checks downstream.',
            placement: 'top',
        },
        {
            target: '#fef-anchor-credit',
            title: 'Credit balances',
            content:
                'Read-only mirrors of the customer\'s credit limit, exposure and headroom from the credit module. If headroom is insufficient, the enquiry needs credit-team approval before it can advance.',
            placement: 'top',
        },
        {
            target: '#fef-anchor-product',
            title: 'Product',
            content:
                'Bunker grade and quantity. Use Add Another Product if the stem covers multiple grades on the same vessel.',
            placement: 'top',
        },
        {
            target: '#fef-anchor-ship',
            title: 'Ship details',
            content:
                'Vessel name, IMO, port, ETA and ETD. The vessel autocomplete pulls from previously-used vessels to keep typing minimal.',
            placement: 'top',
        },
        {
            target: '.fef-header-actions .fef-btn-secondary',
            title: 'Save Draft',
            content:
                'Saves the enquiry without advancing it. Useful when you only have partial information and want to come back later. Drafts live under the Bunker Enquiries tab.',
            placement: 'bottom',
        },
        {
            target: '.fef-header-actions .fef-btn-success',
            title: 'Save & Workflow',
            content:
                'Persists the enquiry AND advances it to the next workflow stage (typically supply-trader handoff). The button is disabled while required fields are missing - hover for the exact list.',
            placement: 'bottom',
        },
        {
            target: '.fuel-enquiry-screen',
            title: 'You are good to go',
            content:
                'That is the enquiry flow. Once it advances past draft, supplier pricing and third-party costs unlock as additional sections. Re-run this tour any time from Hamster Training.',
            placement: 'center',
            disableBeacon: true,
            isLast: true,
        },
    ],
};

export default bunkersEnquiryTour;
